// /Users/pramod/Desktop/school-erp/frontend/src/Tenant/TenantWebsite/sections/Leadership.js

import React from 'react';
import { FiMail, FiLinkedin, FiTwitter, FiFacebook, FiInstagram, FiYoutube, FiGithub, FiGlobe } from 'react-icons/fi';
import buildResolvedFile from '../utils/buildResolvedFile';
import useBrandColors from '../utils/useBrandColors';

const getImage = (item, apiBaseURL) => {
  const candidate = item.image || item.photo || item.imageUrl || item.avatar || '';
  return buildResolvedFile(candidate, apiBaseURL).resolvedSrc || '';
};

const LeadershipCard = ({ item, apiBaseURL }) => {
  const leaderImg = getImage(item, apiBaseURL);
  const socialLinks = item.socialLinks || [];
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();
  
  const getSocialIcon = (platform) => {
    const map = {
      'linkedin': FiLinkedin,
      'twitter': FiTwitter,
      'facebook': FiFacebook,
      'instagram': FiInstagram,
      'youtube': FiYoutube,
      'github': FiGithub,
      'website': FiGlobe,
    };
    return map[platform?.toLowerCase()] || null;
  };

  return (
    <div 
      className="group bg-white rounded-2xl overflow-hidden border transition-all duration-500 hover:shadow-2xl hover:-translate-y-1"
      style={{ borderColor: `${primary}15` }}
    >
      <div 
        className="relative aspect-[4/3] overflow-hidden"
        style={{ 
          background: `linear-gradient(135deg, ${primary}10, ${secondary}10)`
        }}
      >
        {leaderImg ? (
          <img
            src={leaderImg}
            alt={item.name}
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span 
              className="text-6xl font-bold"
              style={{ 
                color: primary,
                fontFamily: fontHeading,
              }}
            >
              {item.name?.charAt(0) || 'L'}
            </span>
          </div>
        )}
      </div>

      <div className="p-6" style={{ fontFamily: fontBody }}>
        <h4 
          className="text-xl font-bold mb-0.5"
          style={{ 
            color: '#1F2937',
            fontFamily: fontHeading,
          }}
        >
          {item.name}
        </h4>
        
        {item.designation && (
          <p className="text-sm font-medium mb-2" style={{ color: primary }}>
            {item.designation}
          </p>
        )}

        {item.qualifications && (
          <p className="text-xs mb-1" style={{ color: '#6B7280' }}>
            {item.qualifications}
          </p>
        )}

        {item.experience && (
          <p className="text-xs mb-3" style={{ color: '#9CA3AF' }}>
            {item.experience} experience
          </p>
        )}

        {item.message && (
          <p className="text-sm leading-relaxed italic mb-4" style={{ color: '#6B7280' }}>
            "{item.message}"
          </p>
        )}

        {item.researchInterests && Array.isArray(item.researchInterests) && item.researchInterests.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {item.researchInterests.slice(0, 3).map((interest, idx) => (
              <span
                key={idx}
                className="px-2.5 py-1 rounded-full text-xs"
                style={{ 
                  backgroundColor: `${primary}10`,
                  color: '#6B7280',
                }}
              >
                {interest}
              </span>
            ))}
            {item.researchInterests.length > 3 && (
              <span 
                className="px-2.5 py-1 rounded-full text-xs"
                style={{ 
                  backgroundColor: `${primary}05`,
                  color: '#9CA3AF',
                }}
              >
                +{item.researchInterests.length - 3}
              </span>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          {item.email && (
            <a
              href={`mailto:${item.email}`}
              className="p-2 rounded-lg transition-colors"
              style={{ 
                backgroundColor: `${primary}10`,
                color: primary,
              }}
              aria-label="Email"
            >
              <FiMail className="w-4 h-4" />
            </a>
          )}
          
          {socialLinks.map((link, idx) => {
            const Icon = getSocialIcon(link.platform);
            if (!Icon) return null;
            return (
              <a
                key={idx}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 rounded-lg transition-colors"
                style={{ 
                  backgroundColor: `${primary}10`,
                  color: primary,
                }}
                aria-label={link.platform}
              >
                <Icon className="w-4 h-4" />
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const Leadership = ({ leadership, apiBaseURL = '' }) => {
  const { sectionTitle, sectionSubtitle, displayStyle = 'grid', items = [] } = leadership || {};
  const { primary, secondary, fontBody, fontHeading } = useBrandColors();

  if (!items || items.length === 0) return null;

  return (
    <section id="leadership" className="py-16 md:py-24 bg-gray-50/30">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {(sectionTitle || sectionSubtitle) && (
            <div className="text-center mb-12 md:mb-16">
              <span 
                className="inline-block text-xs font-light tracking-[0.2em] uppercase mb-3"
                style={{ color: secondary }}
              >
                Leadership
              </span>
              {sectionTitle && (
                <h2 
                  className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3"
                  style={{ 
                    color: primary,
                    fontFamily: fontHeading,
                  }}
                >
                  {sectionTitle}
                </h2>
              )}
              {sectionSubtitle && (
                <p 
                  className="text-lg font-light max-w-2xl mx-auto"
                  style={{ 
                    color: '#6B7280',
                    fontFamily: fontBody,
                  }}
                >
                  {sectionSubtitle}
                </p>
              )}
              <div className="mt-6 flex justify-center">
                <div 
                  className="h-1 w-20 rounded-full" 
                  style={{ 
                    background: `linear-gradient(90deg, ${primary}, ${secondary})` 
                  }} 
                />
              </div>
            </div>
          )}

          <div className={`grid gap-6 ${
            displayStyle === 'grid' 
              ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
              : 'grid-cols-1 md:grid-cols-3 gap-6'
          }`}>
            {items.map((item) => (
              <LeadershipCard 
                key={item._id || item.id || item.name} 
                item={item} 
                apiBaseURL={apiBaseURL}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Leadership;